import React, { useEffect, useState } from "react";
import { Loader2, Star, Trash2, CheckCircle2, MessageSquare, X } from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { api, unwrap } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { formatDate } from "@/lib/format";

export default function AdminReviews() {
  const { locale } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(null); // review currently being responded to

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/reviews");
      setItems(unwrap(r) || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/reviews/${id}`, body);
      toast.success("Review updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try { await api.delete(`/admin/reviews/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  if (loading) return <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="admin-reviews">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tighter">Reviews</h1>
        <p className="text-[#4B5563] text-sm mt-1">{items.length} total · approved reviews show on the public landing</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.length === 0 && (
          <Card className="rounded-2xl p-8 border border-dashed border-[#E5E7EB] text-center text-[#4B5563]">
            <MessageSquare className="w-7 h-7 mx-auto mb-2 text-[#9CA3AF]" />
            No reviews yet. Customers can submit a review from their tracking page after a booking is in-progress or completed.
          </Card>
        )}
        {items.map((r) => (
          <Card key={r.id} className={`rounded-2xl p-5 border ${r.is_approved ? "border-emerald-200 bg-emerald-50/30" : "border-[#E5E7EB]"}`} data-testid={`review-${r.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex text-[#FACC15]">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4" fill={i < r.rating ? "currentColor" : "none"} stroke="currentColor" />)}
                </div>
                <div className="mt-2 text-sm leading-relaxed text-[#0C1A1A]">{r.comment || <span className="text-[#9CA3AF]">(no comment)</span>}</div>
                <div className="text-xs text-[#4B5563] mt-2">
                  <span className="font-semibold">{r.customer_name}</span>
                  {r.customer_city && <> · {r.customer_city}</>}
                  {" · "}<span className="font-mono">{r.booking_number}</span>
                </div>
                {r.admin_response && (
                  <div className="mt-3 p-2.5 rounded-lg bg-white border border-[#E5E7EB] text-xs">
                    <div className="font-bold text-[#0EA5A4] uppercase tracking-wider text-[10px]">Our reply</div>
                    <div className="text-[#0C1A1A] mt-1">{r.admin_response}</div>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-col">
                <Switch checked={!!r.is_approved}
                  onCheckedChange={(v) => patch(r.id, { is_approved: v })}
                  data-testid={`review-approve-${r.id}`} />
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563]">Approve</Label>
              </div>
            </div>
            <div className="mt-4 flex gap-2 items-center">
              <Button onClick={() => setResponding(r)} variant="outline" size="sm" className="rounded-xl" data-testid={`review-respond-${r.id}`}>
                <MessageSquare className="w-3.5 h-3.5 mr-1" /> {r.admin_response ? "Edit reply" : "Reply"}
              </Button>
              <Button onClick={() => remove(r.id)} variant="outline" size="sm" className="rounded-xl text-red-500" data-testid={`review-delete-${r.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
              <div className="ml-auto text-xs text-[#9CA3AF]">{formatDate(r.created_at, locale)}</div>
            </div>
          </Card>
        ))}
      </div>

      {responding && (
        <Card className="rounded-2xl p-6 border border-[#0EA5A4]" data-testid="review-response-editor">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-bold">Reply to {responding.customer_name}</h3>
            <button onClick={() => setResponding(null)}><X className="w-5 h-5" /></button>
          </div>
          <Textarea rows={3} defaultValue={responding.admin_response || ""}
            id="reply-text" className="rounded-xl" placeholder="Thanks for the feedback!"
            data-testid="review-response-input" />
          <div className="mt-3 flex gap-2">
            <Button onClick={() => {
              const text = document.getElementById("reply-text").value;
              patch(responding.id, { admin_response: text });
              setResponding(null);
            }} className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="review-response-save">Save reply</Button>
            <Button onClick={() => setResponding(null)} variant="outline" className="rounded-xl">Cancel</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
